import { motion } from "framer-motion";
import { LucideIcon } from "lucide-react";
import { 
  sectionVariant, 
  staggerContainer, 
  itemVariant,
  viewportOnce,
  viewportOnceNear
} from "@/lib/motion";

export interface TechnologyCategory {
  title: string;
  icon: LucideIcon;
  items: string[];
}

interface TechnologyStackProps {
  label?: string;
  title: string;
  description?: string;
  categories: TechnologyCategory[];
}

const TechnologyStack = ({ 
  label = "Technology", 
  title, 
  description,
  categories 
}: TechnologyStackProps) => {
  return (
    <section className="py-10 md:py-14 lg:py-20 border-t border-border bg-muted/20">
      <div className="container-wide">
        {/* Section Header */}
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-8 md:mb-12 lg:mb-14"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          <motion.span 
            variants={sectionVariant}
            className="text-xs uppercase tracking-[0.2em] text-primary font-medium mb-4 block"
          >
            {label}
          </motion.span>
          <motion.h2 
            variants={sectionVariant}
            className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-4"
          >
            {title}
          </motion.h2>
          {description && (
            <motion.p 
              variants={sectionVariant}
              className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto"
            >
              {description}
            </motion.p>
          )}
        </motion.div>

        {/* Technology Cards Grid - Single row on desktop, responsive on mobile */}
        <motion.div 
          className={`grid gap-5 grid-cols-1 sm:grid-cols-2 ${
            categories.length === 3 ? 'lg:grid-cols-3' :
            categories.length === 4 ? 'lg:grid-cols-4' :
            categories.length === 5 ? 'lg:grid-cols-5' :
            categories.length === 6 ? 'lg:grid-cols-6' :
            'lg:grid-cols-4'
          }`}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnceNear}
          variants={staggerContainer}
        >
          {categories.map((category) => {
            const IconComponent = category.icon;
            return (
              <motion.div 
                key={category.title}
                variants={itemVariant}
                className="bg-background border border-border rounded-lg p-6 hover:border-primary/30 hover:shadow-sm transition-all duration-300"
              >
                {/* Icon */}
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-5">
                  <IconComponent className="h-6 w-6 text-primary" strokeWidth={1.5} />
                </div>
                
                {/* Category Title */}
                <h3 className="text-base font-semibold text-foreground mb-4">
                  {category.title}
                </h3>
                
                {/* Items List */}
                <ul className="space-y-2">
                  {category.items.map((item) => (
                    <li 
                      key={item} 
                      className="text-sm text-muted-foreground flex items-center gap-2"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary/50 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default TechnologyStack;
